import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */

// Keep this component extending the theme Component base class.

// Declarative on:* handlers, refs, and connected/disconnected lifecycle all depend on it.

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this)
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }
  disconnectedCallback() {
  super.disconnectedCallback();

  document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
  document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
}
  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }
  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  
  onLineItemRemove(line) {
    this.removeLineItem({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }
  removeLineItem(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }
  /**
   * Gets the selected tier key based on total quantity.
   * @param {number} totalQty - The total quantity.
   * @returns {string} The tier key.
   */
  
  #getSelectedTierKey(totalQty) {
    if (totalQty >= 500) return '500+';
    if (totalQty >= 100) return '100-499';
    if (totalQty >= 50) return '50-99';
    if (totalQty >= 1) return '1-49';
    return '';
  }
  /**
   * Updates the quantity and wholesale tier.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  
  updateQuantity(config) {
  const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

  this.#disableCartItems();

  const { line, quantity } = config;

  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const sectionsToUpdate = new Set([this.sectionId]);

  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionsToUpdate.add(item.dataset.sectionId);
    }
  });

  fetch(`${Theme.routes.cart_url}.js`, fetchConfig('json'))
    .then((response) => response.json())
    .then((cart) => {
      const currentItem = cart.items[line - 1];

      if (!currentItem) {
        throw new Error(`Cart line ${line} not found`);
      }

      let totalQty = 0;

      cart.items.forEach((item, index) => {
        if (index + 1 === line) {
          totalQty += quantity;
        } else {
          totalQty += item.quantity;
        }
      });

      const newTier = this.#getSelectedTierKey(totalQty);

      /**
       * The wholesale type comes from the logged-in customer's tags, falling
       * back to any line that already carries one. Every line then gets that
       * type and the tier for the new cart total, so lines added without
       * properties (e.g. before login, or via quick add) are brought in line and
       * lines that were not edited do not keep a stale tier.
       */
      const wholesaleType =
        Theme.wholesaleType ||
        cart.items.find((item) => item.properties?._wholesale_type)?.properties._wholesale_type ||
        '';

      const withWholesaleProperties = (item) => ({
        ...(item.properties || {}),
        _wholesale_type: wholesaleType,
        _wholesale_tier: newTier,
      });

      const needsWholesaleUpdate = (item) =>
        item.properties?._wholesale_type !== wholesaleType || item.properties?._wholesale_tier !== newTier;

      const changeCart = (body) =>
        fetch(`${Theme.routes.cart_url}/change.js`, fetchConfig('json', {
          body: JSON.stringify(body),
        })).then((response) => response.json());

      // Lines are addressed by key because updating properties changes a line's
      // position/key and can merge it into an identical line.
      let updateOtherLines = Promise.resolve();

      if (wholesaleType && newTier) {
        cart.items.forEach((item, index) => {
          if (index + 1 === line || !needsWholesaleUpdate(item)) return;

          updateOtherLines = updateOtherLines
            .then(() =>
              changeCart({
                id: item.key,
                quantity: item.quantity,
                properties: withWholesaleProperties(item),
              })
            )
            .then((result) => {
              if (result.errors || result.description) {
                throw new Error(result.errors || result.description);
              }
            });
        });
      }

      const body = {
        id: currentItem.key,
        quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      };

      /**
       * Set wholesale line properties only when the item is not being removed.
       * For removal, quantity: 0 is enough.
       */
      if (quantity > 0 && wholesaleType) {
        body.properties = withWholesaleProperties(currentItem);
      }

      return updateOtherLines.then(() => changeCart(body));
    })
    .then((response) => {
      resetShimmer(this);

      if (response.errors || response.description) {
        this.#handleCartError(line, {
          errors: response.errors || response.description,
        });
        return;
      }

      const sectionHTML = response.sections?.[this.sectionId];

      const newCartItemCount = response.item_count || 0;

      this.#updateQuantitySelectors(response);

      this.dispatchEvent(
        new CartUpdateEvent(response, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: response.sections,
        })
      );

      if (sectionHTML) {
        morphSection(this.sectionId, sectionHTML);
        this.#updateCartQuantitySelectorButtonStates();
      } else {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    });
}
  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  }
  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  }
  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  }
  /**
   * Disables the cart items.
   */
  
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }
  /**
   * Enables the cart items.
   */
  
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }
  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }
  /**
   * Updates button states for all cart quantity selector components.
   */
  
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }
  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}
if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
